import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle } from "@fluentui/react-components";
import { useNavigate, useParams } from "react-router-dom";
import { FileReference } from "../OiReport";
import { AnalysisOrder } from "../redux/analysisOrder";
import { deleteOiAnalysisOrder, GetOiAnalysisOrder, UpdateOiAnalysisOrder } from "../../../services/AnalysisOrderService";
import { useToast } from "../../ToastProvider";
import { getReviewStatusLabel, normalizePreparationAndRoutingData, normalizeSamples } from "./utils";
import { Clipboard24Regular } from "@fluentui/react-icons";
import { email } from "../../../App";
import { definitionNeedsPreparation, getAnalysisDefinition } from "./analysisConfig";
import OiPageHeader, { OiHeaderButton } from "../OiPageHeader";
import { AnalysisOrderSteps } from "./analysisOrderSteps";
import { forgetOiLastRoute } from "../lastRoute";
import { useAppDispatch } from "../../../app/hooks";
import { requestOiTreeRefresh } from "../../../Slices/OiTreeSlice";
import OiLoadingSpinner from "../OiLoadingSpinner";
import { toDisplayDecimal } from "../../../utils/decimal";

interface AnalysisOrderReadViewProps {
  onOpenFileGallery?: (files: FileReference[], initialIndex: number) => void;
}

export default function AnalysisOrderReadView({ onOpenFileGallery: _onOpenFileGallery }: AnalysisOrderReadViewProps) {
  const { uuid } = useParams<{ uuid: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  const [order, setOrder] = useState<AnalysisOrder | null>(null);
  const [orderId, setOrderId] = useState("");
  const [createdAt, setCreatedAt] = useState("");
  const [lastUpdated, setLastUpdated] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const loadOrder = useCallback(async () => {
    if (!uuid) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await GetOiAnalysisOrder(uuid);
      if (!response) {
        forgetOiLastRoute(`/Oi/analysisOrder/${uuid}`);
        return;
      }

      const savedOrder = JSON.parse(response.jsonData) as AnalysisOrder;
      setOrder({
        ...savedOrder,
        isDraft: response.isDraft ?? savedOrder.isDraft,
        isSubmittedForReview: response.isSubmittedForReview ?? savedOrder.isSubmittedForReview,
        reviewPassed: response.reviewPassed === undefined ? savedOrder.reviewPassed : response.reviewPassed,
      });
      setOrderId(response.orderId || "");
      setCreatedAt(response.createdAt || "");
      setLastUpdated(response.lastUpdated || "");
    } catch (error) {
      console.error("Failed to load analysis order", error);
    } finally {
      setIsLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    loadOrder();
  }, [loadOrder]);

  const reviewStatus = useMemo(() => order ? getReviewStatusLabel(order) : "", [order]);

  const submitForReview = async () => {
    if (!uuid || !order) {
      return;
    }

    const payload = JSON.stringify({
      ...order,
      author: order.author || email,
      requestedBy: order.requestedBy || email,
      isDraft: false,
      isSubmittedForReview: true,
      reviewPassed: null,
      samples: normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples),
    });

    const success = await UpdateOiAnalysisOrder(uuid, payload, false, true, null);
    if (success) {
      showToast({ title: "Submitted for review", intent: "success", body: `Analysis order ${orderId} is ready for review.` });
      dispatch(requestOiTreeRefresh());
      await loadOrder();
    } else {
      showToast({ title: "Submission failed", intent: "error", body: "Could not submit the analysis order for review." });
    }
  };

  const updateReviewState = async (reviewPassed: boolean) => {
    if (!uuid || !order) {
      return;
    }

    const payload = JSON.stringify({
      ...order,
      author: order.author || email,
      requestedBy: order.requestedBy || email,
      isDraft: false,
      isSubmittedForReview: true,
      reviewPassed,
      samples: normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples),
    });

    const success = await UpdateOiAnalysisOrder(uuid, payload, false, true, reviewPassed);
    if (success) {
      showToast({ title: reviewPassed ? 'Review passed' : 'Review failed', intent: 'success', body: `Analysis order ${orderId} updated.` });
      dispatch(requestOiTreeRefresh());
      await loadOrder();
    } else {
      showToast({ title: 'Update failed', intent: 'error', body: 'Could not update review state.' });
    }
  };

  if (isLoading) {
    return (
      <OiLoadingSpinner />
    );
  }

  if (!order) {
    return <div className="p-6">Analysis order not found.</div>;
  }

  const selectedAnalyses = order.selectedAnalysesJsonData?.selectedAnalyses ?? [];
  const preparationAndRouting = normalizePreparationAndRoutingData(order.preparationAndRoutingJsonData, selectedAnalyses);
  const normalizedSamples = normalizeSamples(order.linkedPowderOrderLabel || order.linkedPowderOrder, order.samples);
  const selectedAnalysisDefinitions = selectedAnalyses.map(analysisKey => getAnalysisDefinition(analysisKey) ?? {
    key: analysisKey,
    title: analysisKey,
    cardDescription: "",
    routeOptions: [],
  });
  const selectedAnalysesRequirePreparation = selectedAnalysisDefinitions.some(definitionNeedsPreparation);

  const SummaryCard = ({ title, value }: { title: string; value: React.ReactNode }) => (
    <div className="bg-white dark:bg-oi-dark-background p-3 text-primary-dark dark:text-primary-light drop-shadow-md dark:drop-shadow-none">
      <div className="text-xs font-bold uppercase tracking-wide text-primary-dark dark:text-primary-light">{title}</div>
      <div className="mt-1 text-base text-oi-button-blue dark:text-oi-powder-form-yellow">{value || "-"}</div>
    </div>
  );

  return (
    <div className="w-full max-w-none mx-6 pb-4 text-primary-dark dark:text-primary-light">
      <div className="w-full">
        <OiPageHeader
          icon={<Clipboard24Regular />}
          title="Analysis order"
          id={orderId}
          createdAt={createdAt}
          updatedAt={lastUpdated}
          steps={<AnalysisOrderSteps currentStep={3} />}
          actions={<>
            <OiHeaderButton variant="secondary" onClick={() => navigate(`/Oi/analysisOrderEdit/${uuid}`)}>Back</OiHeaderButton>
            <OiHeaderButton variant="primary" onClick={() => navigate(`/Oi/analysisOrderEdit/${uuid}`)}>Edit</OiHeaderButton>
            {!order.isDraft && order.reviewPassed === true && (
              <OiHeaderButton variant="primary" onClick={() => navigate(`/Oi/analysisResult/order/${uuid}`)}>Open result</OiHeaderButton>
            )}
            {(order.isDraft || (!order.isSubmittedForReview && order.reviewPassed === null)) && (
              <OiHeaderButton variant="primary" onClick={submitForReview}>Submit for review</OiHeaderButton>
            )}
            {!order.isDraft && order.isSubmittedForReview && order.reviewPassed === false && (
              <OiHeaderButton variant="primary" onClick={submitForReview}>Send to review</OiHeaderButton>
            )}
            {!order.isDraft && order.isSubmittedForReview && order.reviewPassed !== true && (
              <OiHeaderButton variant="success" onClick={() => updateReviewState(true)}>Approve review</OiHeaderButton>
            )}
            {!order.isDraft && order.isSubmittedForReview && order.reviewPassed !== true && order.reviewPassed !== false && (
              <OiHeaderButton variant="danger" onClick={() => updateReviewState(false)}>Fail review</OiHeaderButton>
            )}
            <OiHeaderButton variant="secondary" onClick={() => setShowDeleteDialog(true)}>Delete</OiHeaderButton>
          </>}
        />


        <div className="bg-white p-4 drop-shadow-md dark:bg-oi-dark-background dark:drop-shadow-none xl:max-w-[760px]">
          <h2 className="mb-3 text-lg font-bold leading-none">4. Review and submit</h2>
          <div className="mb-4 text-sm text-primary-dark/70 dark:text-primary-light">Status: <span className="text-oi-button-blue dark:text-oi-powder-form-yellow">{reviewStatus}</span></div>
          <div className="grid gap-[1px] bg-primary-dark/10 dark:bg-black/20">
            <div className="grid gap-[1px] md:grid-cols-2">
              <SummaryCard title="Order id" value={orderId || "Draft"} />
              <SummaryCard title="Linked Powder order" value={order.linkedPowderOrderLabel || order.linkedPowderOrder || "Not set"} />
              <SummaryCard title="Requested by" value={order.requestedBy || order.author || "Not set"} />
              <SummaryCard title="Requester department" value={order.requesterDepartment || "Not set"} />
              <SummaryCard title="Order date" value={order.orderDate || "Not set"} />
              <SummaryCard title="Requested delivery date" value={order.requestedDeliveryDate || "Not set"} />
              <SummaryCard title="Priority" value={order.priority} />
              <SummaryCard title="Reason for higher priority" value={order.reasonForHigherPriority || "Not set"} />
            </div>
            <div className="bg-white dark:bg-oi-dark-background p-4">
              <div className="mb-2 text-sm font-bold">Samples</div>
              <div className="mt-3 grid grid-cols-[2fr,1fr] bg-primary-dark/10 dark:bg-oi-dark-background px-3 py-2 text-sm font-semibold uppercase text-primary-dark dark:text-primary-light">
                <div>Sample name</div>
                <div>Co % (if known)</div>
              </div>
              {normalizedSamples.map(sample => (
                <div key={sample.sampleId} className="grid grid-cols-[2fr,1fr] border-t border-primary-dark/20 dark:border-primary-light/50 bg-white dark:bg-oi-dark-background px-3 py-4 text-oi-button-blue dark:text-oi-powder-form-yellow">
                  <div>{sample.sampleId}</div>
                  <div>{sample.coPercentage == null ? "-" : toDisplayDecimal(sample.coPercentage)}</div>
                </div>
              ))}
            </div>
            <div className="grid gap-[1px] md:grid-cols-2">
              <SummaryCard title="Return material" value={order.returnMaterial ? "Yes" : "No"} />
              <SummaryCard title="Comment:" value={order.comment || "Not set"} />
              <SummaryCard title="Tagged staff:" value={order.taggedStaff.join(", ") || "Not set"} />
              <SummaryCard title="Tags:" value={order.tags.join(", ") || "Not set"} />
            </div>
            <div className="bg-white dark:bg-oi-dark-background p-4 text-primary-dark dark:text-primary-light drop-shadow-md dark:drop-shadow-none">
              <div className="text-sm font-semibold uppercase tracking-wide text-primary-dark/80 dark:text-primary-light">Attachments:</div>
              <div className="mt-1 flex flex-wrap gap-2 text-sm text-oi-button-blue dark:text-oi-powder-form-yellow">
                {order.uploadedFiles.length > 0 ? order.uploadedFiles.map((file, index) => (
                  <button key={file.uuid} type="button" className="underline" onClick={() => _onOpenFileGallery?.(order.uploadedFiles, index)}>{file.fileName}</button>
                )) : "Not set"}
              </div>
            </div>
            <div className="bg-white dark:bg-oi-dark-background p-4">
              <div className="mb-2 text-sm font-bold">Selected analysis</div>
              <div className="grid gap-4">
                {selectedAnalysesRequirePreparation && (
                  <div>
                    <div className="mb-2 text-sm font-semibold uppercase tracking-wide">Sample preparation</div>
                    <div className="flex flex-wrap gap-2">
                      {preparationAndRouting.noPreparationRequired
                        ? <span className="rounded-full border border-oi-button-blue bg-oi-button-blue px-4 py-2 text-sm text-primary-light">No preparation required</span>
                        : (preparationAndRouting.selectedPreparationMethods.length > 0
                          ? preparationAndRouting.selectedPreparationMethods.map(method => (
                            <span key={method} className="rounded-full border border-oi-button-blue bg-oi-button-blue px-4 py-2 text-sm text-primary-light">{method}</span>
                          ))
                          : <span className="text-sm text-primary-dark dark:text-primary-light">No preparation method selected</span>)}
                    </div>
                  </div>
                )}
                {selectedAnalysisDefinitions.length > 0 ? selectedAnalysisDefinitions.map(analysis => {
                  const selectedOptions = preparationAndRouting.routes[analysis.key]?.selectedOptions ?? [];
                  return (
                    <div key={analysis.key}>
                      <div className="mb-2 text-sm font-semibold uppercase tracking-wide">{analysis.title}</div>
                      <div className="flex flex-wrap gap-2">
                        {selectedOptions.length > 0
                          ? selectedOptions.map(option => (
                            <span key={option} className="rounded-full border border-oi-button-blue bg-oi-button-blue px-4 py-2 text-sm text-primary-light">{option}</span>
                          ))
                          : <span className="text-sm text-primary-dark dark:text-primary-light">No route options selected</span>}
                      </div>
                    </div>
                  );
                }) : <SummaryCard key="none" title="Selected analysis" value="None selected" />}
              </div>
            </div>
            <SummaryCard title="Information for analysis team" value={preparationAndRouting.comment || "Not set"} />
          </div>
        </div>
      </div>

      <Dialog open={showDeleteDialog} onOpenChange={(_, data) => setShowDeleteDialog(data.open)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Delete analysis order</DialogTitle>
            <DialogContent>Delete {orderId}?</DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setShowDeleteDialog(false)}>Back</Button>
              <Button appearance="primary" onClick={async () => {
                if (uuid) {
                  const success = await deleteOiAnalysisOrder(uuid);
                  if (success) {
                    showToast({ title: 'Deleted', intent: 'success', body: `${orderId} deleted.` });
                    dispatch(requestOiTreeRefresh());
                    navigate('/Oi');
                  }
                }
              }}>Delete</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
